import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle, X } from 'lucide-react';

/**
 * FilterPanel - Filtration controls for gap assessment questions
 * Includes activity type, intangible asset type, and impact type filters
 * @param {boolean} showFullQuestionText - Whether to show full question text
 * @param {function} setShowFullQuestionText - Handler to toggle full question text
 * @param {function} onShowScoringGuideHelp - Handler to show scoring guide help modal
 * @param {string|null} activityTypeFilter - Currently selected activity type filter
 * @param {function} setActivityTypeFilter - Handler to set activity type filter
 * @param {string|null} intangibleAssetFilter - Currently selected intangible asset filter
 * @param {function} setIntangibleAssetFilter - Handler to set intangible asset filter
 * @param {string|null} readinessAttractivenessFilter - Currently selected readiness/attractiveness filter
 * @param {function} setReadinessAttractivenessFilter - Handler to set readiness/attractiveness filter
 * @param {string|null} selectedGapFilter - Currently selected gap zone filter
 * @param {function} setSelectedGapFilter - Handler to set gap zone filter
 * @param {function} onShowActivityTypeHelp - Handler to show activity type help modal
 * @param {function} onShowAssetTypeHelp - Handler to show asset type help modal
 * @param {function} onShowImpactTypeHelp - Handler to show impact type help modal
 * @param {Object} activityTypeMapping - Mapping of activity types to question IDs
 * @param {Object} intangibleAssetMapping - Mapping of intangible asset types to question IDs
 * @param {function} getGapZoneInfo - Function to get gap zone information
 * @param {function} setExpandedCategory - Function to set expanded categories
 * @param {Array} finalCategoryKeys - Array of category keys
 * @param {function} getCategoryQuestions - Function to get questions for a category
 */
const FilterPanel = ({
  showFullQuestionText,
  setShowFullQuestionText,
  onShowScoringGuideHelp,
  activityTypeFilter,
  setActivityTypeFilter,
  intangibleAssetFilter,
  setIntangibleAssetFilter,
  readinessAttractivenessFilter,
  setReadinessAttractivenessFilter,
  selectedGapFilter,
  setSelectedGapFilter,
  onShowActivityTypeHelp,
  onShowAssetTypeHelp,
  onShowImpactTypeHelp,
  activityTypeMapping,
  intangibleAssetMapping,
  getGapZoneInfo,
  setExpandedCategory,
  finalCategoryKeys,
  getCategoryQuestions
}) => {
  /**
   * Handles filter changes and auto-expands categories with matching questions
   * @param {string} filterType - Type of filter being changed
   * @param {string|null} newFilter - New filter value
   */
  const handleFilterChange = (filterType, newFilter) => {
    // Update the appropriate filter
    switch (filterType) {
      case 'activity':
        setActivityTypeFilter(newFilter);
        break;
      case 'asset':
        setIntangibleAssetFilter(newFilter);
        break;
      case 'impact':
        setReadinessAttractivenessFilter(newFilter);
        break;
      default:
        break;
    }

    // Get current filter values (with the new filter applied)
    const currentActivityFilter = filterType === 'activity' ? newFilter : activityTypeFilter;
    const currentAssetFilter = filterType === 'asset' ? newFilter : intangibleAssetFilter;
    const currentImpactFilter = filterType === 'impact' ? newFilter : readinessAttractivenessFilter;

    // Auto-expand categories with matching questions
    if (currentActivityFilter || currentAssetFilter || currentImpactFilter) {
      const categoriesToExpand = [];
      finalCategoryKeys.forEach(catKey => {
        const categoryQuestions = getCategoryQuestions(catKey);
        const hasMatchingQuestions = categoryQuestions.some(q => {
          // Check activity type - try both id and question_id
          if (currentActivityFilter) {
            const activityIds = activityTypeMapping[currentActivityFilter] || [];
            if (!activityIds.includes(q.question_id) && !activityIds.includes(q.id)) return false;
          }
          // Check intangible asset type - try both id and question_id
          if (currentAssetFilter) {
            const assetIds = intangibleAssetMapping[currentAssetFilter] || [];
            if (!assetIds.includes(q.question_id) && !assetIds.includes(q.id)) return false;
          }
          // Check readiness/attractiveness
          if (currentImpactFilter) {
            if (currentImpactFilter === 'Readiness' && !q.impacts_readiness) return false;
            if (currentImpactFilter === 'Attractiveness' && !q.impacts_attractiveness) return false;
          }
          return true;
        });
        if (hasMatchingQuestions) {
          categoriesToExpand.push(catKey);
        }
      });
      setExpandedCategory(categoriesToExpand.length > 0 ? categoriesToExpand : []);
    } else {
      setExpandedCategory([]);
    }
  };

  /**
   * Clears a specific filter and collapses categories if no filters remain
   * @param {string} filterType - Type of filter to clear
   */
  const handleClearFilter = (filterType) => {
    switch (filterType) {
      case 'activity':
        setActivityTypeFilter(null);
        break;
      case 'asset':
        setIntangibleAssetFilter(null);
        break;
      case 'impact':
        setReadinessAttractivenessFilter(null);
        break;
      case 'gap':
        setSelectedGapFilter(null);
        break;
      default:
        break;
    }

    // Collapse categories if no other filters are active
    const hasOtherFilters =
      (filterType !== 'activity' && activityTypeFilter) ||
      (filterType !== 'asset' && intangibleAssetFilter) ||
      (filterType !== 'impact' && readinessAttractivenessFilter) ||
      (filterType !== 'gap' && selectedGapFilter);

    if (!hasOtherFilters) {
      setExpandedCategory([]);
    }
  };

  /**
   * Clears all active filters
   */
  const handleClearAllFilters = () => {
    setActivityTypeFilter(null);
    setIntangibleAssetFilter(null);
    setReadinessAttractivenessFilter(null);
    setSelectedGapFilter(null);
    setExpandedCategory([]);
  };

  const hasActiveFilters = activityTypeFilter || intangibleAssetFilter || readinessAttractivenessFilter || selectedGapFilter;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-gray-900">Category Breakdown</h3>
        <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showFullQuestionText}
            onChange={(e) => setShowFullQuestionText(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span>Show full question text</span>
        </label>
      </div>

      {/* Scoring Guide Help Button */}
      <div className="mb-3">
        <button
          onClick={onShowScoringGuideHelp}
          className="inline-flex items-center space-x-1.5 text-blue-600 hover:text-blue-800 transition px-2 py-1 rounded-md hover:bg-blue-50"
          title="Learn more about Clear, Documented, & Transferable"
        >
          <HelpCircle size={16} />
          <span className="text-xs font-medium">What does "Clear, Documented, & Transferable" mean?</span>
        </button>
      </div>

      {/* Filter Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Activity Type Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Activity Type
            </label>
            <button
              onClick={onShowActivityTypeHelp}
              className="text-blue-600 hover:text-blue-800 transition"
              title="Learn more about Activity Types"
            >
              <HelpCircle size={16} />
            </button>
          </div>
          <select
            value={activityTypeFilter || ''}
            onChange={(e) => handleFilterChange('activity', e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Activities</option>
            <option value="De-Risking Activity">De-Risking Activity</option>
            <option value="Strategy Activity">Strategy Activity</option>
            <option value="Efficiency Activity">Efficiency Activity</option>
            <option value="Growth Activity">Growth Activity</option>
            <option value="Culture Activity">Culture Activity</option>
          </select>
        </div>

        {/* Intangible Asset Type Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Intangible Asset Type
            </label>
            <button
              onClick={onShowAssetTypeHelp}
              className="text-blue-600 hover:text-blue-800 transition"
              title="Learn more about Intangible Asset Types"
            >
              <HelpCircle size={16} />
            </button>
          </div>
          <select
            value={intangibleAssetFilter || ''}
            onChange={(e) => handleFilterChange('asset', e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Asset Types</option>
            <option value="Human">Human</option>
            <option value="Structural">Structural</option>
            <option value="Customer">Customer</option>
            <option value="Social">Social</option>
            <option value="Revenue & Profitability">Revenue & Profitability</option>
          </select>
        </div>

        {/* Readiness/Attractiveness Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Impact Type
            </label>
            <button
              onClick={onShowImpactTypeHelp}
              className="text-blue-600 hover:text-blue-800 transition"
              title="Learn more about Readiness & Attractiveness"
            >
              <HelpCircle size={16} />
            </button>
          </div>
          <select
            value={readinessAttractivenessFilter || ''}
            onChange={(e) => handleFilterChange('impact', e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            <option value="">All Impact Types</option>
            <option value="Readiness">Readiness</option>
            <option value="Attractiveness">Attractiveness</option>
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Active Filters:</span>
          {activityTypeFilter && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
              {activityTypeFilter}
              <button
                onClick={() => handleClearFilter('activity')}
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          {intangibleAssetFilter && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
              {intangibleAssetFilter}
              <button
                onClick={() => handleClearFilter('asset')}
                className="ml-1 hover:text-green-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          {readinessAttractivenessFilter && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
              {readinessAttractivenessFilter}
              <button
                onClick={() => handleClearFilter('impact')}
                className="ml-1 hover:text-amber-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          {selectedGapFilter && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
              {selectedGapFilter === 'n-a' ? 'N/A' : getGapZoneInfo(selectedGapFilter).label}
              <button
                onClick={() => handleClearFilter('gap')}
                className="ml-1 hover:text-blue-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          <button
            onClick={handleClearAllFilters}
            className="text-xs font-medium text-red-600 hover:text-red-800"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

FilterPanel.propTypes = {
  showFullQuestionText: PropTypes.bool.isRequired,
  setShowFullQuestionText: PropTypes.func.isRequired,
  onShowScoringGuideHelp: PropTypes.func.isRequired,
  activityTypeFilter: PropTypes.string,
  setActivityTypeFilter: PropTypes.func.isRequired,
  intangibleAssetFilter: PropTypes.string,
  setIntangibleAssetFilter: PropTypes.func.isRequired,
  readinessAttractivenessFilter: PropTypes.string,
  setReadinessAttractivenessFilter: PropTypes.func.isRequired,
  selectedGapFilter: PropTypes.string,
  setSelectedGapFilter: PropTypes.func.isRequired,
  onShowActivityTypeHelp: PropTypes.func.isRequired,
  onShowAssetTypeHelp: PropTypes.func.isRequired,
  onShowImpactTypeHelp: PropTypes.func.isRequired,
  activityTypeMapping: PropTypes.object.isRequired,
  intangibleAssetMapping: PropTypes.object.isRequired,
  getGapZoneInfo: PropTypes.func.isRequired,
  setExpandedCategory: PropTypes.func.isRequired,
  finalCategoryKeys: PropTypes.array.isRequired,
  getCategoryQuestions: PropTypes.func.isRequired
};

export default FilterPanel;
